import { useMemo } from 'react';
import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { FetchError } from 'src/types/TApiQueries';
import { AiSettings, AiSettingsUpdatePayload, AiUserBudget, AiUserBudgetUpdatePayload } from 'src/types/TAiSettings';
import { updateAiSettings, updateUserAiBudget } from '../functions/aiSettingsApi';
import { queryFnFetchErrorWrapper } from '../utils/errorHandlers';
import { queryNames } from '../utils/queryNames';

export function useUpdateAiSettingsMutation(
  mutationOptions?: UseMutationOptions<AiSettings, FetchError, AiSettingsUpdatePayload>,
) {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper((data: AiSettingsUpdatePayload) => updateAiSettings(data, accessToken)),
    [accessToken],
  );

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: async (result, ...args) => {
      await queryClient.invalidateQueries([queryNames.aiSettings]);
      mutationOptions?.onSuccess?.(result, ...args);
    },
  });
}

export function useUpdateUserAiBudgetMutation(
  userId: number,
  mutationOptions?: UseMutationOptions<AiUserBudget, FetchError, AiUserBudgetUpdatePayload>,
) {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper((data: AiUserBudgetUpdatePayload) => updateUserAiBudget(userId, data, accessToken)),
    [userId, accessToken],
  );

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: async (result, ...args) => {
      await queryClient.invalidateQueries([queryNames.userAiBudget, userId]);
      mutationOptions?.onSuccess?.(result, ...args);
    },
  });
}
